import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Message,
  PartResult,
  SummaryItem,
  FoundPartRef,
  ValidationPartsPayload,
} from '../components/features/chat';
import { buildResolvedPartHeader } from '../components/features/chat/MultiPartResultView';
import { positionChipsFromExtra } from '../utils/partLabel';
import { isSupersededResult } from '../utils/partEdits';
import { RequestLine } from '../components/features/chat/types';
import {
  ledgerFromConversation,
  ledgerFromMessage,
  trackerItem,
  unsettledNames,
} from '../utils/requestLines';

/**
 * Requested-parts tracker state, derived from the conversation.
 *
 * Extracted from ChatPage so the GUEST chat renders the same floating
 * PartsTrackerPanel instead of falling back to the legacy in-bubble parts list
 * (BotMessage renders that fallback whenever `onValidationParts` is absent —
 * which is exactly what made guest sessions still show the old list). Both
 * pages must derive rows identically or the tracker and the results table stop
 * agreeing on labels, counts and "N options" badges.
 *
 * Pass the conversation and the active session id; reset happens on session
 * change. Wire the result into BotMessage (`onValidationParts`,
 * `validationItems`) and PartsTrackerPanel.
 */
export function useTrackedParts(messages: Message[], activeSessionId: string | null | undefined) {
  // The validation list arrives before the first search frame does, so it is
  // what the tracker shows while that first search runs. Once any search frame
  // has landed the ledger is authoritative and this is no longer read.
  const [validationParts, setValidationParts] = useState<SummaryItem[]>([]);
  const [trackedServiceTitle, setTrackedServiceTitle] = useState<string | undefined>(undefined);
  const handleValidationParts = useCallback((payload: ValidationPartsPayload) => {
    setValidationParts(payload.items);
    if (payload.serviceTitle) setTrackedServiceTitle(payload.serviceTitle);
  }, []);
  // Reset the tracker when switching sessions.
  useEffect(() => {
    setValidationParts([]);
    setTrackedServiceTitle(undefined);
  }, [activeSessionId]);
  // Annotated analysis images across the whole session (deduped, in order) —
  // shown inside the tracker so the analysis context travels with the list.
  const trackerAnnotatedImages = useMemo<string[]>(() => {
    const seen = new Set<string>();
    const urls: string[] = [];
    for (const msg of messages) {
      if (msg.sender !== 'bot' || !msg.annotated_images?.length) continue;
      for (const url of msg.annotated_images) {
        if (url && !seen.has(url)) { seen.add(url); urls.push(url); }
      }
    }
    return urls;
  }, [messages]);

  // {part → photo} for vision-identified parts, newest frame wins per part —
  // each tracker row shows its own photo instead of one shared strip.
  const trackerVisionPartImages = useMemo(() => {
    const byPart = new Map<string, { part: string; image_url: string }>();
    for (const msg of messages) {
      if (msg.sender !== 'bot') continue;
      for (const vp of msg.vision_part_images ?? []) {
        if (vp?.part && vp.image_url) byPart.set(vp.part, vp);
      }
    }
    return Array.from(byPart.values());
  }, [messages]);

  /** Clear the tracker — for an explicit "new chat", where the session id may
   *  not change and the session-change effect above would not fire. */
  const resetTrackedParts = useCallback(() => {
    setValidationParts([]);
    setTrackedServiceTitle(undefined);
  }, []);

  // ── Request-line ledger (ADR 0007) ────────────────────────────────────────
  // The newest ledger in the conversation is the current account of the order:
  // one row per request, numbered once, with an outcome per line. Reading it
  // means the tracker and the results table cannot disagree, because neither is
  // deriving the pairing any more.
  // The order as the whole conversation knows it: the backend's ledger when a
  // message carries one, otherwise one derived from what each message said
  // about itself. One derivation, so the tracker and the results table cannot
  // disagree about what was asked for or how it went.
  const ledgerLines = useMemo<RequestLine[]>(
    () => ledgerFromConversation(
      messages.filter(m => m.sender === 'bot') as any,
      (pr: PartResult) => positionChipsFromExtra((pr as any).extra_details),
    ),
    [messages],
  );

  const ledgerItems = useMemo<SummaryItem[]>(
    () => ledgerLines.map(line => trackerItem(line) as SummaryItem),
    [ledgerLines],
  );

  /** The newest card for each line, with where to scroll to see it. */
  const ledgerFoundParts = useMemo<FoundPartRef[]>(() => {
    const byLine = new Map<string, { part: PartResult; parts: PartResult[]; anchorId: string }>();
    for (const msg of messages) {
      if (msg.sender !== 'bot' || !msg.parts_results?.length) continue;
      const { bindings } = ledgerFromMessage(
        msg as any,
        (pr: PartResult) => positionChipsFromExtra((pr as any).extra_details),
      );
      const seen = bindings;
      // A later message re-answering a line replaces the earlier reference.
      seen.forEach((indices, id) => byLine.set(id, {
        part: msg.parts_results![indices[0]],
        parts: indices.map(i => msg.parts_results![i]),
        anchorId: `${msg.id}-part-${indices[0]}`,
      }));
    }
    const refs: FoundPartRef[] = [];
    ledgerLines.forEach((line, index) => {
      if (line.status !== 'found') return;
      const hit = byLine.get(line.line_id);
      if (!hit) return;
      const header = buildResolvedPartHeader(hit.part, line.label || line.original);
      refs.push({
        name: line.canonical || line.label || line.original,
        label: header.label || line.label,
        probability: header.probability,
        hasWarnings: header.hasWarnings,
        resolutions: hit.part.resolutions,
        resolutions_explanation: hit.part.resolutions_explanation,
        resolution_keys: hit.part.resolution_keys,
        anchorId: hit.anchorId,
        optionCount: hit.parts.length,
        // The row's own position in the ledger. Both sides of the tracker's
        // pairing carry it, so the panel matches by index and never by name.
        reqIndex: index,
        positions: positionChipsFromExtra((hit.part as any).extra_details),
      });
    });
    return refs;
  }, [ledgerLines, messages]);

  const ledgerNotFound = useMemo<string[]>(() => unsettledNames(ledgerLines), [ledgerLines]);

  return {
    // The ledger is what every surface renders. The validation list stands in
    // only before this session's first search frame, so the tracker is never
    // blank while that search runs.
    trackedParts: ledgerItems.length > 0 ? ledgerItems : validationParts,
    trackedServiceTitle,
    resetTrackedParts,
    handleValidationParts,
    trackerFoundParts: ledgerFoundParts,
    trackerAnnotatedImages,
    trackerVisionPartImages,
    trackerNotFoundParts: ledgerNotFound,
    /** The order's current lines (newest ledger) — e.g. for the skipped bar. */
    trackerLines: ledgerLines,
  };
}
